const CAP4 = {
  id: "fundamentos-blockchain-4",
  slug: "tokens-erc20",
  title: "Tokens ERC-20",
  sections: [
    {
      id: "hero",
      heading: "Tokens ERC-20: Estándar Fundamental del Ecosistema Ethereum",
      text: `El estándar ERC-20 es uno de los pilares técnicos más importantes del ecosistema Ethereum. 
Define cómo deben comportarse los tokens fungibles dentro de la red, permitiendo que cualquier aplicación descentralizada (dApp) 
interactúe con ellos de manera uniforme y predecible. 
Gracias a este estándar, fue posible crear miles de tokens compatibles, impulsando el auge de las finanzas descentralizadas (DeFi), 
los intercambios automatizados (DEX) y la economía tokenizada moderna.`
    },
    {
      id: "introduccion",
      heading: "Introducción y Contexto Histórico",
      text: `Ethereum fue lanzado oficialmente en 2015 por Vitalik Buterin con el propósito de superar las limitaciones del diseño original de Bitcoin. 
Mientras Bitcoin solo permitía transacciones monetarias simples, Ethereum introdujo un entorno programable mediante contratos inteligentes (smart contracts), 
lo que posibilitó crear tokens personalizados, automatizar acuerdos y desplegar aplicaciones descentralizadas (dApps).`,
      list: [
        {
          title: "Motivación para un nuevo estándar",
          text: `Antes del ERC-20, cada token en Ethereum se implementaba con su propia lógica y funciones personalizadas, 
lo que generaba incompatibilidades entre carteras, exchanges y plataformas DeFi. 
Era necesario un conjunto de reglas unificadas para garantizar interoperabilidad.`
        },
        {
          title: "Nacimiento del estándar ERC-20",
          text: `El término "ERC" significa *Ethereum Request for Comments*. 
El estándar ERC-20 fue propuesto por Fabian Vogelsteller en 2015 como un protocolo para crear tokens fungibles 
que pudieran transferirse y utilizarse de forma coherente en todo el ecosistema Ethereum. 
Desde entonces, se ha convertido en la base técnica sobre la cual se han construido miles de proyectos.`
        },
        {
          title: "Ether (ETH) como moneda nativa",
          text: `Ether es la criptomoneda principal de Ethereum y actúa como medio de intercambio, reserva de valor y unidad de cuenta. 
Aunque ETH no es un token ERC-20 (ya que pertenece al protocolo base), inspiró el diseño del estándar para permitir 
la creación de tokens que se comportaran de manera similar dentro de la misma red.`
        }
      ]
    },
    {
      id: "estructura-erc20",
      heading: "Estructura y Funciones del Estándar ERC-20",
      text: `El estándar ERC-20 define una interfaz mínima de funciones que todo contrato debe implementar 
para ser reconocido como un token compatible dentro de Ethereum. 
Esta interfaz garantiza que los tokens puedan ser leídos y utilizados por cualquier aplicación o contrato inteligente.`,
      list: [
        {
          title: "Funciones básicas del estándar ERC-20",
          text: `1. **totalSupply()** – Devuelve el suministro total de tokens en circulación.  
2. **balanceOf(address)** – Muestra el saldo de una dirección específica.  
3. **transfer(address, uint256)** – Envía tokens directamente de una dirección a otra.  
4. **approve(address, uint256)** – Autoriza a otra dirección (por ejemplo, un contrato) a gastar tokens en nombre del titular.  
5. **transferFrom(address, address, uint256)** – Permite transferencias entre usuarios mediadas por contratos autorizados.  
6. **allowance(address, address)** – Muestra cuánto puede gastar un tercero autorizado (delegado).`
        },
        {
          title: "Eventos estándar",
          text: `El ERC-20 incluye dos eventos que registran las acciones principales en la blockchain:  
- **Transfer**: se emite cada vez que se transfieren tokens entre direcciones.  
- **Approval**: se emite cuando un titular concede permiso a otro contrato o usuario para gastar sus tokens.`
        },
        {
          title: "Interoperabilidad garantizada",
          text: `Cualquier wallet o aplicación que implemente correctamente estas funciones puede operar con cualquier token ERC-20, 
lo que permite un ecosistema interoperable donde los contratos y los usuarios pueden interactuar sin fricción.`
        }
      ]
    },
    {
      id: "ventajas",
      heading: "Ventajas del Estándar ERC-20",
      text: `El éxito del estándar ERC-20 se debe a su simplicidad, su adopción masiva y su alta compatibilidad con herramientas del ecosistema Ethereum.`,
      list: [
        {
          title: "Compatibilidad universal",
          text: `Los tokens ERC-20 pueden almacenarse y transferirse usando las mismas carteras y contratos, 
reduciendo la complejidad técnica para usuarios y desarrolladores.`
        },
        {
          title: "Facilidad de desarrollo",
          text: `Los contratos ERC-20 son fáciles de implementar, auditar y desplegar, 
gracias a plantillas abiertas y bibliotecas como OpenZeppelin que ofrecen implementaciones seguras y actualizadas.`
        },
        {
          title: "Base de la economía DeFi",
          text: `Las principales plataformas financieras descentralizadas —Uniswap, Aave, Compound, Curve— se basan en tokens ERC-20 
para representar activos, préstamos, recompensas o stablecoins.`
        },
        {
          title: "Estandarización y transparencia",
          text: `La existencia de una especificación pública y abierta reduce el riesgo de errores o incompatibilidades entre contratos 
y facilita las auditorías de seguridad.`
        }
      ]
    },
    {
      id: "limitaciones",
      heading: "Limitaciones y Desafíos del ERC-20",
      text: `A pesar de su éxito, el estándar ERC-20 presenta ciertas limitaciones técnicas que motivaron el desarrollo de nuevos estándares como ERC-777 o ERC-1155.`,
      list: [
        {
          title: "Falta de detección de errores en transferencias",
          text: `Si una transferencia ERC-20 falla (por ejemplo, por saldo insuficiente), 
el contrato no siempre devuelve un mensaje claro o revertido, lo que puede generar confusión en los usuarios.`
        },
        {
          title: "Problemas con aprobaciones duplicadas",
          text: `El método approve() puede ser vulnerable si se cambia la cantidad aprobada antes de usarla, 
lo que abre la puerta a ataques de *race condition* si no se maneja correctamente.`
        },
        {
          title: "Costos de gas elevados",
          text: `Cada transferencia o interacción requiere gas, lo que puede volverse costoso durante periodos de congestión en la red Ethereum.`
        },
        {
          title: "No apto para NFTs o activos únicos",
          text: `Al ser un estándar para tokens fungibles, el ERC-20 no puede representar objetos únicos o coleccionables; 
para ello se desarrolló el estándar ERC-721.`
        }
      ]
    },
    {
      id: "ejemplos",
      heading: "Ejemplos Reales de Tokens ERC-20",
      text: `Miles de proyectos utilizan el estándar ERC-20 para emitir sus propios tokens con distintos fines. Algunos de los más reconocidos son:`,
      list: [
        {
          title: "USDT (Tether)",
          text: `Stablecoin anclada al dólar estadounidense, ampliamente usada para pagos y comercio.`
        },
        {
          title: "LINK (Chainlink)",
          text: `Token del oráculo descentralizado Chainlink, que conecta contratos inteligentes con datos del mundo real.`
        },
        {
          title: "UNI (Uniswap)",
          text: `Token de gobernanza de la plataforma Uniswap, que permite participar en votaciones y en el control del protocolo.`
        },
        {
          title: "DAI (MakerDAO)",
          text: `Stablecoin descentralizada respaldada por colateral, que mantiene paridad con el dólar mediante contratos inteligentes.`
        }
      ]
    },
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `El estándar ERC-20 no solo facilitó la creación de tokens dentro de Ethereum, sino que impulsó la innovación en todo el ecosistema blockchain. 
Su diseño simple, interoperable y abierto permitió la expansión de proyectos de todo tipo: desde stablecoins hasta sistemas de gobernanza descentralizada. 
Aunque nuevas versiones buscan superar sus limitaciones, el ERC-20 sigue siendo la base técnica sobre la cual se construyó gran parte de la Web3 moderna.`
    }
  ]
};

export default CAP4;
